// ENG-4832: reap MCP child processes whose env references a credential
// that's just been rotated, so the next call respawns them with the
// fresh value.
//
// Why this exists: the manager polls /host/refresh, gets new OAuth
// tokens, writes them into the agent's `.env.integrations`. But the
// MCP children (xero-mcp-server, gmail-mcp, etc.) were spawned by
// Claude Code at session start and hold their **spawn-time** env. They
// never re-read .env.integrations, so once the access_token in their
// env block expires (typically 30 min for Xero), every upstream call
// 401s — and the agent reports "refresh failed" because its view of
// the token is stale, regardless of what the DB row says. See
// ENG-4832 for the full triage (Stirling, Day 3).
//
// The fix: when integration credentials rotate, identify which MCP
// children reference the rotated env var(s) AND belong to this agent,
// SIGTERM them. Claude Code's MCP transport detects the dead child
// and respawns it on next request — fresh env, fresh token, agent
// keeps working. Conversation context is preserved.
//
// Companion to orphan-channel-mcp-reaper.ts (ENG-4808). Same shape
// (parse ps, walk ppid chain, kill with grace) but a different
// targeting rule: that one finds children whose parent claude is
// dead; this one finds children whose env is stale.
//
// Pure helpers exposed for tests:
//   • parseEnvIntegrationsVars()   — extract var names from a
//     .env.integrations file
//   • findMcpServersUsingVars()    — given the parsed .mcp.json and
//     a set of changed var names, which server keys are affected?
//   • findMcpChildrenForAgent()    — walk ps rows, return PIDs
//     belonging to <codeName>'s claude AND matching one of the
//     server-key argv signatures
//
// Side-effecting:
//   • reapStaleMcpChildren()       — orchestrates the above + SIGTERM
//     / grace / SIGKILL. Idempotent — calling repeatedly is safe.

import { execFileSync } from 'node:child_process';
import { hardBoundedExecFile, type BoundedExecResult } from './bounded-exec.js';
import { parsePsRows, type PsRow } from './orphan-channel-mcp-reaper.js';

/**
 * ENG-9781 — turn a bounded probe result into a liveness verdict.
 *
 * Exported because it is the whole of the semantic change and it deserves to be
 * asserted directly rather than through the reaper's full call shape (where
 * every existing test injects `isAlive` and would never reach this code).
 *
 * The old shape was `catch { return false }`: any failure meant dead. Under an
 * unbounded docker call that was mostly hidden, because a wedged daemon never
 * returned at all. Bounding the call WITHOUT fixing this would have converted a
 * hang into a wrong answer — every live MCP child declared dead the moment
 * dockerd got slow — and this verdict is what authorises a reap.
 *
 *   ok                  → alive
 *   failed with status  → dead   (a real answer: `kill -0` said no such process)
 *   anything else       → ALIVE  (no answer; fail-safe)
 *
 * Refusing to reap on no information costs one more cycle. Reaping on no
 * information costs a session.
 */
export function livenessFromProbe(
  r: BoundedExecResult,
  onNoAnswer?: (why: string) => void,
): boolean {
  if (r.kind === 'ok') return true;
  if (r.kind === 'failed' && r.status !== null) return false;
  onNoAnswer?.(r.kind === 'timed-out' ? 'did not answer within its bound' : `could not run: ${r.message}`);
  return true;
}


// ENG-5344: rotation grace window — when reapStaleMcpChildren SIGTERMs
// an MCP child, Claude Code's MCP transport only respawns on the next
// tool call. There is therefore a window (≥ SIGTERM-grace + respawn
// latency) where the child is dead and presence-reaper would see zero
// live children for the affected server keys, even though nothing is
// actually broken. Without coordination, the next manager tick after a
// rotation kills the whole tmux session — observed on Stirling at ~20
// min intervals, 54 false-positive restarts/day.
//
// We record (codeName, serverKey) → SIGTERM timestamp here and let
// presence-reaper consult it to skip keys that are still in their
// post-rotation respawn window.
const rotationTimestamps = new Map<string, number>();

/**
 * Default rotation grace window. Sized to cover:
 *   - the SIGTERM → SIGKILL grace in reapStaleMcpChildren (5s default)
 *   - Claude Code's lazy respawn latency on next tool call
 *   - a healthy margin so a slow respawn doesn't trip presence-reaper
 *
 * 15s is comfortably above the observed ~2s race window between
 * stale-reaper and the next manager tick.
 */
export const DEFAULT_ROTATION_GRACE_MS = 15_000;

function rotationKey(codeName: string, serverKey: string): string {
  return `${codeName}\x00${serverKey}`;
}

/**
 * Record that we just SIGTERM'd one or more MCP children for these
 * server keys belonging to <codeName>. Callers should invoke this after
 * SIGTERM (not before): no kill = no in-flight rotation to grace.
 *
 * Idempotent — the latest call wins so back-to-back rotations extend
 * the grace window rather than expire it early.
 */
export function recordStaleRotation(
  codeName: string,
  serverKeys: Iterable<string>,
  now: () => number = Date.now,
): void {
  const ts = now();
  for (const key of serverKeys) {
    rotationTimestamps.set(rotationKey(codeName, key), ts);
  }
}

/**
 * Was <codeName>:<serverKey> rotated within the last `withinMs`?
 * Returns false if no rotation has been recorded for the pair.
 *
 * Self-cleaning: expired entries are deleted on read so the map stays
 * bounded under steady-state load.
 */
export function wasRecentlyRotated(
  codeName: string,
  serverKey: string,
  withinMs: number,
  now: () => number = Date.now,
): boolean {
  const k = rotationKey(codeName, serverKey);
  const ts = rotationTimestamps.get(k);
  if (ts === undefined) return false;
  if (now() - ts < withinMs) return true;
  rotationTimestamps.delete(k);
  return false;
}

/**
 * ENG-8225: how a (codeName, serverKey) pair stands relative to its last
 * recorded rotation.
 *
 *   'none'         — no rotation on record (or the record aged past retention).
 *   'within-grace' — rotated less than `graceMs` ago; a respawn is legitimately
 *                    in flight and "no live children" is not a fault.
 *   'expired'      — rotated longer than `graceMs` ago. Combined with "still no
 *                    live children" this is the ENG-8225 hole: the grace window
 *                    banked on a tool call that never came.
 */
export type RotationStatus = 'none' | 'within-grace' | 'expired';

/**
 * ENG-8225: how long a rotation record is retained AFTER its grace window
 * expires, so a caller can tell "the rotation-driven respawn never happened"
 * (→ 'expired') from "this key was never rotated" (→ 'none').
 *
 * {@link wasRecentlyRotated} deletes on an expired read, which collapses those
 * two cases into one `false` and is exactly why the expiry was unobservable
 * before this ticket. Retention has to outlast the whole prompt-probe +
 * escalation cycle in mcp-presence-reaper (3 probes spaced 30s, then escalate)
 * with generous headroom, while staying bounded so the map can't grow without
 * limit on a host that rotates credentials all day.
 */
export const ROTATION_TRACKING_RETENTION_MS = 15 * 60 * 1000;

/**
 * ENG-8225: non-destructive tri-state read of the rotation tracker.
 *
 * Unlike {@link wasRecentlyRotated} this does NOT delete on grace expiry — the
 * record survives until `retentionMs`, which is what lets mcp-presence-reaper
 * distinguish "rotated, grace expired, child still absent" (arm a prompt probe)
 * from "plain missing" (restart as before). Past `retentionMs` the entry is
 * deleted and 'none' is returned, keeping the map bounded.
 */
export function classifyRotation(
  codeName: string,
  serverKey: string,
  graceMs: number,
  now: () => number = Date.now,
  retentionMs: number = ROTATION_TRACKING_RETENTION_MS,
): RotationStatus {
  const k = rotationKey(codeName, serverKey);
  const ts = rotationTimestamps.get(k);
  if (ts === undefined) return 'none';
  const age = now() - ts;
  // Grace wins even if a caller passes a graceMs above the retention window —
  // an in-flight respawn must never be reported as a stalled one.
  if (age < graceMs) return 'within-grace';
  if (age < retentionMs) return 'expired';
  rotationTimestamps.delete(k);
  return 'none';
}

/**
 * ENG-8225: the SIGTERM timestamp of the rotation currently on record for
 * (codeName, serverKey), or `undefined` when there is none.
 *
 * This timestamp IS the rotation's identity, not merely a property of it: both
 * the grace window and the retention window in {@link classifyRotation} are
 * measured from it, and {@link recordStaleRotation} overwrites it on every new
 * SIGTERM. So a caller that keeps per-rotation state alongside the tracker (the
 * prompt-probe budget in mcp-presence-reaper) can compare its stored stamp
 * against this value to tell "my state belongs to the rotation I'm looking at"
 * from "my state is a leftover describing a rotation that is over". Without
 * that, key-keyed state outlives the rotation that created it and silently
 * governs the next, unrelated one.
 *
 * Non-destructive: reading never evicts (contrast {@link wasRecentlyRotated}),
 * so the identity is stable across the retention window. Read
 * {@link classifyRotation} for the STATUS; use this only for identity.
 *
 * Note two rotations recorded in the same millisecond are indistinguishable
 * here. That is deliberate and matches `recordStaleRotation`'s documented
 * "latest call wins" semantics — same-millisecond kills share one grace window,
 * so they are one rotation as far as every other consumer is concerned too.
 */
export function getRotationRecordedAt(codeName: string, serverKey: string): number | undefined {
  return rotationTimestamps.get(rotationKey(codeName, serverKey));
}

/**
 * ENG-8225: forget the rotation record(s) for <codeName>. Called once the
 * rotated child is observed LIVE again — the rotation is complete, so a later
 * disappearance is a fresh fault and must not be attributed to this rotation.
 */
export function clearStaleRotation(codeName: string, serverKeys: Iterable<string>): void {
  for (const key of serverKeys) {
    rotationTimestamps.delete(rotationKey(codeName, key));
  }
}

/**
 * Test-only seam: clear the rotation tracker between tests so a recorded
 * rotation in one case doesn't bleed into another.
 */
export function __resetStaleRotationTrackerForTests(): void {
  rotationTimestamps.clear();
}

/**
 * Parse a `.env.integrations` file body into a Map of var name → raw
 * value (the value is captured verbatim, including any shell-quote
 * wrapping the writer added). Pure — operate on file contents, not
 * paths. Robust to:
 *   - leading/trailing whitespace
 *   - shell-quote forms (`X='value'`, `X="value"`, `X=raw`)
 *   - comments (lines starting with `#`)
 *   - blank lines
 *   - duplicates (later wins, matching shell-source semantics)
 *
 * Values are NOT dequoted on purpose — for the diff use case we just
 * need byte-for-byte equality between two writes of the same value,
 * and writeIntegrations is deterministic (always emits the same
 * shellQuote form for the same input). Skipping dequoting also keeps
 * this immune to quote-handling bugs.
 */
export function parseEnvIntegrationsEntries(content: string): Map<string, string> {
  const entries = new Map<string, string>();
  for (const raw of content.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const eq = line.indexOf('=');
    if (eq <= 0) continue;
    const name = line.slice(0, eq).trim();
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) continue;
    const value = line.slice(eq + 1);
    entries.set(name, value);
  }
  return entries;
}

/**
 * Just the SET of var names declared in a `.env.integrations` body.
 * Convenience wrapper over parseEnvIntegrationsEntries — preserves
 * the original ENG-4832 API for callers that only need names.
 */
export function parseEnvIntegrationsVars(content: string): string[] {
  return [...parseEnvIntegrationsEntries(content).keys()];
}

/**
 * Diff two `.env.integrations` parses. Returns the names whose value
 * changed (rotated), was added, or was removed between the two
 * snapshots.
 *
 * Used by the manager-worker integration-rotation hook to pass the
 * **precisely-rotated** subset to findMcpServersUsingVars rather than
 * "every var currently present in the file", which would over-reap
 * unrelated MCP children on every refresh tick (CodeRabbit on PR #797).
 */
export function diffEnvIntegrations(
  oldContent: string | undefined,
  newContent: string,
): string[] {
  const oldEntries = oldContent === undefined ? new Map<string, string>() : parseEnvIntegrationsEntries(oldContent);
  const newEntries = parseEnvIntegrationsEntries(newContent);
  const changed = new Set<string>();
  for (const [name, value] of newEntries) {
    if (oldEntries.get(name) !== value) changed.add(name);
  }
  // Removed vars also count — a server whose env was depending on a
  // now-absent var has effectively been rotated to "no value", which
  // is just as breaking as a value change.
  for (const name of oldEntries.keys()) {
    if (!newEntries.has(name)) changed.add(name);
  }
  return [...changed];
}

/**
 * A `.env.integrations` diff split by KIND of change. `diffEnvIntegrations`
 * flattens all three into one "what changed" list (all the reap path needs);
 * this classifier keeps them apart for the one caller that must tell a benign
 * value-only re-mint from a real membership change.
 */
export interface EnvIntegrationsDiff {
  /** Present in BOTH snapshots, value changed — a server-side rotation / re-mint. */
  rotated: string[];
  /** Present in new but not old — a credential / integration added. */
  added: string[];
  /** Present in old but not new — a credential / integration removed. */
  removed: string[];
}

/**
 * ENG-7541: classify an `.env.integrations` diff into value-only ROTATIONS
 * (present before and after, only the value changed — the GitHub App token
 * re-mint case) versus membership ADDs / REMOVEs. The manager uses this to tell
 * a benign per-poll credential re-mint (which must never pause the agent — it is
 * tagged with the non-tripping `credential-rotation` breaker reason) apart from
 * a real integration add/remove (which wants an MCP rebind and counts toward the
 * provisioning breaker as `hot-reload-mcp`).
 *
 * Semantics are the source of truth for {@link diffEnvIntegrations}: the flat
 * "changed" set that function returns is exactly `rotated ∪ added ∪ removed`.
 *
 * Pure and side-effect-free so the classification is unit-testable in isolation.
 */
export function classifyEnvIntegrationsDiff(
  oldContent: string | undefined,
  newContent: string,
): EnvIntegrationsDiff {
  const oldEntries = oldContent === undefined ? new Map<string, string>() : parseEnvIntegrationsEntries(oldContent);
  const newEntries = parseEnvIntegrationsEntries(newContent);
  const rotated: string[] = [];
  const added: string[] = [];
  const removed: string[] = [];
  for (const [name, value] of newEntries) {
    if (!oldEntries.has(name)) added.push(name);
    else if (oldEntries.get(name) !== value) rotated.push(name);
  }
  for (const name of oldEntries.keys()) {
    if (!newEntries.has(name)) removed.push(name);
  }
  return { rotated, added, removed };
}

/**
 * Pure: given a parsed `.mcp.json` and a set of env-var names that
 * have just rotated, return the MCP server keys whose `env` block
 * references any of those vars via `${VAR}` substitution.
 *
 * Only the `${VAR}` form is checked because that's the only form
 * Claude Code substitutes at MCP launch time from the parent claude's
 * env. A literal value-equals-name (e.g. `"TOKEN": "XERO_ACCESS_TOKEN"`
 * with no `${}`) is just a constant string the MCP child sees as-is —
 * the rotated value never reaches it, so reaping the child wouldn't
 * fix anything (the child would just respawn with the same broken
 * literal). CodeRabbit on PR #797 caught a doc-vs-impl drift that
 * previously claimed both forms were checked; this comment is now
 * aligned with the implementation, and the test
 * `does NOT match servers whose env contains the var name as a
 * literal value` pins the correct behaviour.
 */
export interface McpServerEntry {
  command?: string;
  args?: string[];
  env?: Record<string, string>;
}

export interface McpConfig {
  mcpServers?: Record<string, McpServerEntry>;
}

export function findMcpServersUsingVars(
  mcp: McpConfig | null | undefined,
  changedVars: Iterable<string>,
): string[] {
  const changedSet = new Set(changedVars);
  if (!mcp?.mcpServers || changedSet.size === 0) return [];

  const result: string[] = [];
  for (const [serverKey, entry] of Object.entries(mcp.mcpServers)) {
    if (!entry || typeof entry !== 'object') continue;
    const env = entry.env;
    if (!env || typeof env !== 'object') continue;
    let matches = false;
    for (const value of Object.values(env)) {
      if (typeof value !== 'string') continue;
      // Match ${VAR} substitution form first — the common case.
      const placeholderMatches = value.match(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g);
      if (placeholderMatches) {
        for (const ph of placeholderMatches) {
          const name = ph.slice(2, -1);
          if (changedSet.has(name)) {
            matches = true;
            break;
          }
        }
      }
      if (matches) break;
    }
    if (matches) result.push(serverKey);
  }
  return result;
}

/**
 * ENG-7510: given the vars that changed in `.env.integrations` and the parsed
 * `.mcp.json`, return the subset that require a full agent SESSION RESPAWN to
 * take effect — the env-only / CLI-tool integration vars.
 *
 * GitHub is the canonical case: `GITHUB_ACCESS_TOKEN` / `GITHUB_TOKEN` live in
 * the agent's own process env (backing the `gh` CLI + curl), not an MCP server's
 * env block, so the child-reap hot-reload path (`findMcpServersUsingVars` +
 * `reapStaleMcpChildren`) can never pick them up. The wrapper sources
 * `.env.integrations` exactly once at spawn (no live re-read), so the running
 * agent stays blind to an added/removed var of this kind until it is bounced.
 *
 * A var needs a respawn when it is BOTH:
 *   - not a channel secret (`channelSecretKeys`) — those have their own
 *     launch-time respawn watcher (checkChannelSecretDriftAndScheduleRestart,
 *     ENG-6062); including them here would double-restart; AND
 *   - referenced by NO MCP server (`${VAR}` substitution) — an MCP-referenced
 *     var is handled by reaping that child (hot-reload), no full respawn needed.
 *
 * Pure and side-effect-free so the respawn decision is unit-testable in
 * isolation from the manager poll loop.
 */
export function envOnlyRespawnVars(
  changedVars: Iterable<string>,
  mcp: McpConfig | null | undefined,
  channelSecretKeys: readonly string[],
): string[] {
  const channelSet = new Set(channelSecretKeys);
  return [...changedVars].filter(
    (v) => !channelSet.has(v) && findMcpServersUsingVars(mcp, [v]).length === 0,
  );
}

/**
 * ENG-7748: env-var names that a stdio remote-MCP proxy reads LIVE from the token
 * file per request, via its `AGT_REMOTE_MCP_EXTRA_HEADERS` (the `header:VAR`
 * list). A change to one of these needs NEITHER a child reap NOR a session
 * respawn: the proxy serves the new value on the very next request. Anchor's
 * frequently re-minted `ANCHOR_BROWSER_SESSION_ID` is the case this exists for -
 * without the exemption `envOnlyRespawnVars` would schedule a respawn on every
 * re-mint (the var isn't `${VAR}`-referenced, so it looks env-only), defeating
 * the whole point of routing anchor through the proxy.
 *
 * Covers only the `AGT_REMOTE_MCP_EXTRA_HEADERS` axis. For the primary
 * credential var (`AGT_REMOTE_MCP_TOKEN_VAR`) see {@link liveProxyTokenVars}
 * (ENG-7965).
 *
 * Pure and side-effect-free.
 */
export function liveProxyExtraHeaderVars(mcp: McpConfig | null | undefined): Set<string> {
  const out = new Set<string>();
  if (!mcp?.mcpServers) return out;
  for (const entry of Object.values(mcp.mcpServers)) {
    const spec = entry?.env?.['AGT_REMOTE_MCP_EXTRA_HEADERS'];
    if (typeof spec !== 'string') continue;
    for (const pair of spec.split(',')) {
      const colon = pair.indexOf(':');
      if (colon <= 0) continue;
      const varName = pair.slice(colon + 1).trim();
      if (varName) out.add(varName);
    }
  }
  return out;
}

/**
 * ENG-7965: env-var names that a stdio remote-MCP proxy uses as its primary
 * bearer credential, declared via `AGT_REMOTE_MCP_TOKEN_VAR`. The proxy
 * (remote-oauth-proxy.ts) reads this var from the per-agent secrets file with
 * `readFileSync` on EVERY request, so a mid-session token rotation is picked
 * up automatically - no MCP child reap and no agent session restart are needed.
 *
 * This exemption fixes the confirmed case where OAuth rotations for integrations
 * such as brand-ninja and Xero caused spurious agent session restarts, dropping
 * in-flight tasks (Stirling × 2, Jack × 1 on 2026-07-22 04:14–04:44 UTC).
 *
 * The var looks "env-only" to `envOnlyRespawnVars` (it is not referenced via a
 * `${VAR}` placeholder in `.mcp.json` - the literal var NAME is the value of
 * `AGT_REMOTE_MCP_TOKEN_VAR`), so without this exemption the manager schedules a
 * session restart on every credential rotation.
 *
 * Pure and side-effect-free.
 */
export function liveProxyTokenVars(mcp: McpConfig | null | undefined): Set<string> {
  const out = new Set<string>();
  if (!mcp?.mcpServers) return out;
  for (const entry of Object.values(mcp.mcpServers)) {
    const tokenVar = entry?.env?.['AGT_REMOTE_MCP_TOKEN_VAR'];
    if (typeof tokenVar === 'string' && tokenVar.trim()) {
      out.add(tokenVar.trim());
    }
  }
  return out;
}

/**
 * Compile MCP-server-key signatures into argv match patterns. Each MCP
 * server is spawned as some variant of `npx -y <package>` or
 * `node <path>` — the running child's argv contains the package
 * binary name (e.g. `xero-mcp-server`) which is stable enough to
 * match against. Falling back to the raw server-key as a literal
 * (e.g. `xero`) catches the cases where the package name and the
 * mcpServers key match.
 */
/**
 * Build precise argv-matchers from each server's actual `.mcp.json`
 * `command + args`, NOT from the bare server key.
 *
 * Pre-fix this function used `\b<serverKey>\b` against argv, which on
 * a server key like `xero` would also match `xero-other-mcp-server` —
 * any sibling package that happens to share a prefix would get bounced
 * along with the intended target (CodeRabbit on PR #797). The fix:
 * extract a precise per-server signature from the entry's command +
 * args. For an `npx -y @xeroapi/xero-mcp-server@latest` entry we end
 * up matching against:
 *   1. the full package spec (`@xeroapi/xero-mcp-server`) — matches
 *      the npm-exec wrapper process
 *   2. the bin basename (`xero-mcp-server`) — matches the actual node
 *      child whose argv is `node /path/to/.bin/xero-mcp-server`
 *
 * Both forms are needed because the parent npm-exec process and the
 * child node process have different argv shapes but share the same
 * package identity.
 *
 * Falls back to a `(?<![A-Za-z0-9_])${key}(?![A-Za-z0-9_])` matcher
 * (alnum/underscore boundary, but `-` and `/` allowed) when the entry
 * has no resolvable package signature — the bare-key form is still
 * tighter than the original `\b...\b` because `_` is now part of the
 * "name continuation" set, so a server key `xero` doesn't match
 * `xero_thing` either.
 */
function buildArgvMatchersForEntry(key: string, entry: McpServerEntry | undefined): RegExp[] {
  const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const patterns: RegExp[] = [];

  // Try to extract a precise package signature from the args. The
  // common shapes we provision through buildMcpJson:
  //   npx -y @scope/package@version
  //   npx -y package@version
  //   node /absolute/path/to/.bin/package-name
  // Walk args from last to first, looking for the first token that
  // looks like a package spec or an absolute path with a bin basename.
  const args = entry?.args ?? [];
  for (let i = args.length - 1; i >= 0; i--) {
    const arg = args[i];
    if (typeof arg !== 'string' || !arg) continue;
    // Skip flags
    if (arg.startsWith('-')) continue;

    // Strip @version suffix from npm package specs.
    const stripped = arg.replace(/@[^/@]*$/, (m) =>
      // @latest, @1.2.3, etc. — drop. But @scope at the START of a
      // package spec must NOT be stripped, so we only strip a tail
      // `@...` if it doesn't itself start with a slash inside.
      // The regex above only matches a single trailing `@...`
      // segment after the last `/`, so this is safe for `@scope/pkg`.
      m.includes('/') ? m : '',
    );

    // Tail-boundary that disallows the package-name continuation
    // characters: alnum, `_`, `-`. Without this, matching
    // `@integrity-labs/cloud-broker` would also match
    // `@integrity-labs/cloud-broker-experimental` — exactly the
    // overmatch class CodeRabbit's review on PR #797 flagged.
    const tail = '(?![A-Za-z0-9_-])';

    // Looks like an npm package spec? (`@scope/pkg` or `pkg-name`)
    if (/^@?[a-z0-9]([a-z0-9._-]*\/)?[a-z0-9._-]+$/i.test(stripped)) {
      patterns.push(new RegExp(`${escapeRe(stripped)}${tail}`));
      // Also push the basename if the spec is scoped — matches the
      // node child whose argv only has the bin name.
      const basename = stripped.split('/').pop();
      if (basename && basename !== stripped) {
        patterns.push(new RegExp(`${escapeRe(basename)}${tail}`));
      }
      break;
    }

    // Looks like a path? Match the basename (the bin name) — the
    // running child's argv typically has the full path.
    if (stripped.includes('/')) {
      const basename = stripped.split('/').pop();
      if (basename) {
        patterns.push(new RegExp(`${escapeRe(basename)}${tail}`));
        break;
      }
    }
  }

  if (patterns.length === 0) {
    // Fallback: bare server key with tighter boundaries than `\b`.
    // Disallow alphanumerics + `_` either side; `-` and `/` are
    // permitted as separators (so e.g. `cloud-broker` matches
    // `@integrity-labs/cloud-broker` correctly).
    const safe = escapeRe(key);
    patterns.push(new RegExp(`(?<![A-Za-z0-9_])${safe}(?![A-Za-z0-9_])`));
    if (safe.includes('_')) {
      const dashed = safe.replace(/_/g, '-');
      patterns.push(new RegExp(`(?<![A-Za-z0-9_])${dashed}(?![A-Za-z0-9_])`));
    }
  }
  return patterns;
}

/** Match the parent claude bound to <codeName> via `--name agt-<codeName>`. */
export function buildClaudeAgentMatcher(codeName: string): RegExp {
  // Restrict to alnum/dash so a malicious codeName can't widen the
  // match (codeName is validated upstream but defence in depth).
  const safe = codeName.replace(/[^A-Za-z0-9_-]/g, '');
  // Use a tail boundary that disallows hyphen-as-word-character —
  // `\b` between `g` and `-` matches in regex (since `-` is not a
  // word char), so `\\b` would let `agt-stirling-other` match the
  // matcher for `stirling`. Require an actual argument boundary
  // (whitespace) or end-of-string instead. CodeRabbit-detected
  // regression class.
  return new RegExp(`\\bclaude\\b.*--name\\s+agt-${safe}(?=\\s|$)`);
}

/**
 * Pure: given parsed ps rows, find MCP child PIDs that belong to
 * <codeName>'s session AND match one of the rotated-env server keys.
 *
 * "Belong to <codeName>'s session" = ppid chain (up to maxDepth)
 * reaches a `claude --name agt-<codeName>` process. Same walk shape
 * as the orphan reaper, but the success direction is inverted: that
 * one collects MCPs whose ancestry does NOT reach a live claude
 * (orphans); this one collects MCPs whose ancestry DOES reach a
 * specific live claude (this agent's claude).
 *
 * Empty `serverKeys` returns []. Empty `psRows` returns [].
 * maxDepth defaults to 8 — same as the orphan reaper.
 */
export function findMcpChildrenForAgent(args: {
  rows: PsRow[];
  codeName: string;
  serverKeys: string[];
  /**
   * Parsed `.mcp.json` so the matcher can derive precise per-server
   * argv signatures from each entry's `command + args`. When omitted,
   * matching falls back to the bare-key form which is tighter than
   * the original `\b...\b` but can still overmatch for short keys.
   * Production callers pass this; some tests pass only `serverKeys`
   * to exercise the fallback path.
   */
  mcpJson?: McpConfig | null;
  maxDepth?: number;
  /**
   * ENG-6660: rows come from `docker exec ps` INSIDE the agent's own container
   * (Docker-isolated agent). The container is a single-agent boundary AND its
   * claude carries no `--name agt-<codeName>` flag, so the strict host matcher
   * can't anchor. We still validate ownership - the ppid walk must reach SOME
   * claude (a relaxed matcher) so a stray argv match without a live claude
   * ancestor isn't counted as a healthy child (CodeRabbit, PR #2345).
   */
  inContainer?: boolean;
}): number[] {
  const { rows, codeName, serverKeys, mcpJson } = args;
  const maxDepth = args.maxDepth ?? 8;
  const inContainer = args.inContainer ?? false;
  if (serverKeys.length === 0 || rows.length === 0) return [];

  // In-container: any `claude` ancestor counts (single-agent boundary, no --name
  // flag). Host: the exact `claude --name agt-<codeName>` parent.
  const claudeMatcher = inContainer ? /\bclaude\b/ : buildClaudeAgentMatcher(codeName);
  // Build precise per-server matchers from each entry's command + args
  // when mcpJson is provided. Falls back to the bare-key form when
  // a key has no resolvable entry (e.g. it was just removed from
  // mcp.json mid-tick or the test passed only serverKeys).
  const argvMatchers: RegExp[] = [];
  for (const key of serverKeys) {
    const entry = mcpJson?.mcpServers?.[key];
    argvMatchers.push(...buildArgvMatchersForEntry(key, entry));
  }
  const byPid = new Map<number, PsRow>(rows.map((r) => [r.pid, r]));

  const matched: number[] = [];
  for (const row of rows) {
    // Must match at least one of the server-key argv patterns.
    if (!argvMatchers.some((re) => re.test(row.args))) continue;
    // Must NOT itself be a claude (we want children, not the agent's
    // claude process).
    if (/\bclaude\b/.test(row.args) && row.args.includes(`--name agt-${codeName}`)) continue;

    // Walk parents looking for the agent's claude (relaxed in-container).
    let cur: PsRow | undefined = byPid.get(row.ppid);
    let belongs = false;
    for (let depth = 0; depth < maxDepth && cur; depth++) {
      if (claudeMatcher.test(cur.args)) {
        belongs = true;
        break;
      }
      if (cur.pid === 1) break;
      cur = byPid.get(cur.ppid);
    }
    if (belongs) matched.push(row.pid);
  }
  return matched;
}

/**
 * Run `ps`, find the stale MCP children for <codeName> belonging to
 * the listed serverKeys, and SIGTERM them. After `graceMs`, SIGKILL
 * any that didn't exit. Returns the list of pids that were sent
 * SIGTERM (informational — tests use this without mocking
 * process.kill).
 *
 * Idempotent. Cheap enough to call on every integration-rotation
 * tick — when nothing matches, the work is one ps invocation + a
 * regex pass.
 */
export function reapStaleMcpChildren(args: {
  log: (msg: string) => void;
  codeName: string;
  serverKeys: string[];
  /**
   * Parsed `.mcp.json` so the matcher can derive precise per-server
   * argv signatures from each entry's command + args. Production
   * callers always pass this; tests can omit to exercise the
   * fallback (bare-key with tightened boundaries).
   */
  mcpJson?: McpConfig | null;
  graceMs?: number;
  /** Test seam: substitute the `ps` invocation. */
  runPs?: () => string;
  /** Test seam: substitute the kill function. */
  killProcess?: (pid: number, signal: 'SIGTERM' | 'SIGKILL') => void;
  /** Test seam: substitute the still-alive check. */
  isAlive?: (pid: number) => boolean;
  /**
   * ENG-6670: when the agent runs under Docker isolation, its MCP children live
   * in the container's PID namespace. The default ps/kill/isAlive then operate
   * via `docker exec agt-<code>`, so we enumerate AND signal the CORRECT
   * (container-namespace) pids — a host `process.kill(<container-pid>)` would
   * hit an unrelated host process. Mirrors mcp-presence-reaper's `isolated`
   * (ENG-6660). Default false = unchanged host behaviour.
   */
  isolated?: boolean;
}): number[] {
  const { log, codeName, serverKeys, mcpJson, graceMs = 5_000, isolated = false } = args;
  if (serverKeys.length === 0) return [];

  // ENG-6670: under Docker isolation, enumerate AND signal inside the container's
  // PID namespace (docker exec). The pids from `docker exec ps` are
  // container-namespace pids — a host process.kill on them would signal an
  // unrelated host process. Mirrors mcp-presence-reaper's isolated runPs.
  const runPs =
    args.runPs ??
    (isolated
      ? // ENG-9781: hard-bounded. See bounded-exec.ts — `execFileSync`'s own
        // `timeout` is not a bound, and this runs per isolated agent per cycle
        // on the manager-worker's critical path. Throws on a non-answer so the
        // caller's existing catch skips the agent rather than acting on an
        // empty process list.
        () => {
          const r = hardBoundedExecFile(
            'docker',
            ['exec', `agt-${codeName}`, 'ps', '-eo', 'pid,ppid,args'],
            { timeoutMs: 8_000, log },
          );
          if (r.kind === 'ok') return r.stdout;
          throw new Error(
            r.kind === 'timed-out'
              ? `docker exec ps did not answer within its bound (bounded=${r.bounded})`
              : `docker exec ps failed (status=${r.status}): ${r.message}`,
          );
        }
      : () => execFileSync('ps', ['-eo', 'pid,ppid,args'], { encoding: 'utf-8', timeout: 5_000 }));
  const killProcess =
    args.killProcess ??
    (isolated
      ? (pid: number, signal: 'SIGTERM' | 'SIGKILL') => {
          // ENG-9781: hard-bounded. Best-effort either way — a signal we could
          // not deliver is the same non-event as a process that had already
          // exited — but it must not be able to hang the manager to find out.
          hardBoundedExecFile(
            'docker',
            ['exec', `agt-${codeName}`, 'kill', signal === 'SIGKILL' ? '-KILL' : '-TERM', String(pid)],
            { timeoutMs: 8_000, log },
          );
        }
      : (pid: number, signal: 'SIGTERM' | 'SIGKILL') => {
          try {
            process.kill(pid, signal);
          } catch {
            // Process already exited — fine.
          }
        });
  const isAlive =
    args.isAlive ??
    (isolated
      ? (pid: number) => {
          // ENG-9781, and this one is a SEMANTIC fix as well as a bound.
          //
          // The old shape was `catch { return false }`: any failure meant dead.
          // Under an unbounded call that was mostly hidden, because a wedged
          // docker never returned at all. Bounding it without touching the
          // semantics would have converted a hang into a WRONG ANSWER — every
          // live MCP child declared dead the moment dockerd got slow, and this
          // verdict is what authorises a reap.
          //
          // So the three cases are now separated: a clean exit is alive, a
          // real non-zero answer is dead, and no answer is treated as ALIVE.
          // Fail-safe, matching persistent-session's docker probe: refusing to
          // reap on no information costs one more cycle, reaping on no
          // information costs a session.
          const r = hardBoundedExecFile(
            'docker',
            ['exec', `agt-${codeName}`, 'kill', '-0', String(pid)],
            { timeoutMs: 8_000, log },
          );
          return livenessFromProbe(r, (why) =>
            log(
              `[stale-mcp-reaper] liveness probe for pid ${pid} in 'agt-${codeName}' ` +
                `${why} — treating as ALIVE (no reap) rather than guessing`,
            ),
          );
        }
      : (pid: number) => {
          try {
            process.kill(pid, 0);
            return true;
          } catch {
            return false;
          }
        });

  let psOutput: string;
  try {
    psOutput = runPs();
  } catch (err) {
    log(`[stale-mcp-reaper] ps invocation failed for '${codeName}': ${(err as Error).message} — skipping reap`);
    return [];
  }

  const rows = parsePsRows(psOutput);
  const targets = findMcpChildrenForAgent({ rows, codeName, serverKeys, mcpJson, inContainer: isolated });
  if (targets.length === 0) return [];

  const byPid = new Map<number, PsRow>(rows.map((r) => [r.pid, r]));
  const describe = (pid: number): string => {
    const argv = byPid.get(pid)?.args ?? '';
    // Pull the first identifiable npm package or script name from argv.
    const pkgMatch = argv.match(/(@[a-z0-9_-]+\/[a-z0-9_-]+|[a-z0-9_-]+-mcp-server|[a-z0-9_-]+-mcp\b)/i);
    return pkgMatch ? `${pkgMatch[1]} (pid ${pid})` : `pid ${pid}`;
  };

  log(
    `[stale-mcp-reaper] '${codeName}': rotating ${targets.length} stale MCP child(ren) for [${serverKeys.join(', ')}]: ${targets.map(describe).join(', ')}`,
  );
  for (const pid of targets) {
    killProcess(pid, 'SIGTERM');
  }
  // ENG-5344: record the rotation so the presence-reaper running on the
  // next manager tick (≥ ~2s later) doesn't see the now-dead children as
  // "declared MCP missing" and tear down the whole tmux session. The
  // child won't respawn until Claude Code makes its next tool call, so a
  // grace window is the only correct read here. Only record for keys we
  // actually killed — recording the full `serverKeys` list would grace
  // keys whose env rotated but had no live children to begin with.
  const killedKeys = new Set<string>();
  for (const pid of targets) {
    const argv = byPid.get(pid)?.args ?? '';
    for (const key of serverKeys) {
      const entry = mcpJson?.mcpServers?.[key];
      const matchers = buildArgvMatchersForEntry(key, entry);
      if (matchers.some((re) => re.test(argv))) killedKeys.add(key);
    }
  }
  if (killedKeys.size > 0) recordStaleRotation(codeName, killedKeys);

  setTimeout(() => {
    try {
      // CodeRabbit on PR #797: re-resolve ownership before SIGKILL.
      // 5s grace is long enough for the kernel to recycle a freed
      // PID into an entirely unrelated process — `targets.filter(isAlive)`
      // alone would happily kill that bystander. Re-running the
      // original ps + match pipeline against fresh process state
      // confirms the PID is still one of *our* MCP children for this
      // agent. Anything that doesn't survive that re-check is either
      // already dead (good) or has been recycled (do NOT kill).
      let freshPsOutput: string;
      try {
        freshPsOutput = runPs();
      } catch (err) {
        log(`[stale-mcp-reaper] '${codeName}': fresh ps for SIGKILL re-verify failed: ${(err as Error).message} — skipping SIGKILL pass`);
        return;
      }
      const stillOwned = new Set(
        findMcpChildrenForAgent({
          rows: parsePsRows(freshPsOutput),
          codeName,
          serverKeys,
          mcpJson,
          inContainer: isolated,
        }),
      );
      const stragglers = targets.filter((pid) => isAlive(pid) && stillOwned.has(pid));
      if (stragglers.length === 0) return;
      log(
        `[stale-mcp-reaper] '${codeName}': ${stragglers.length} child(ren) survived SIGTERM; sending SIGKILL: ${stragglers.map(describe).join(', ')}`,
      );
      for (const pid of stragglers) {
        killProcess(pid, 'SIGKILL');
      }
    } catch (err) {
      log(`[stale-mcp-reaper] '${codeName}': error in SIGKILL pass: ${(err as Error).message}`);
    }
  }, graceMs).unref();

  return targets;
}
